import React, { useState, ChangeEvent, useRef, useEffect } from 'react';
import ImageList from "./imageList";
import { useParagraphEditorDataContext } from "contexts/ParagraphEditorContext";
import { useTranslation } from "react-i18next";
import { ReactComponent as PictureIconSvg } from "@assets/icons/image.svg";
import {
  FileInputLabel,
  HiddenFileInput,
  InfoTextInput,
  LabelWrapper,
  UploaderContainer
} from "./imageUploader.styles";
import { IMedia } from "models/wall";
import { createDraftParagraphMedia, deleteDraftParagraphMedias } from "api/media";
import { toast } from "react-toastify";
import { Input } from "@atoms";


const ImageUploader: React.FC = () => {
  const {t} = useTranslation();
  const {draftParagraph, updateDraftParagraph} = useParagraphEditorDataContext();
  const [images, setImages] = useState<IMedia[]>(draftParagraph.draft_media || []);
  const [text, setText] = useState<string>(draftParagraph.text || "");

  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const openFileDialog = () => {
    if (!isLoading) {
      inputRef.current?.click();
    }
  };

  useEffect(() => {
    updateDraftParagraph({text})
  }, [text]);

  const handleFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    const allowedCount = Math.min(1 - images.length, files.length);

    if (files[0].size / 1024 / 1024 > 10) {
      toast.error(t("imageSizeError"));
      return;
    }

    const promises = files.slice(0, allowedCount).map(async (file) => {
      const res = await createDraftParagraphMedia({
        media_type: 'image',
        file,
        name: file.name,
        draft_paragraph: draftParagraph.uuid
      });
      updateDraftParagraph({draft_media: [...(draftParagraph.draft_media || []), res]});
      return setImages(p => [...p, res]);
    });

    setIsLoading(true);
    try {
      await Promise.all(promises);
    } catch (error) {
      console.error("Error uploading images", error);
    } finally {
      setIsLoading(false);
    }

    e.target.value = "";
  };

  const onDelete = async (uuid: string) => {
    try {
      await deleteDraftParagraphMedias(uuid);
      setImages((p) => p.filter(i => i.uuid !== uuid));
    } catch (e) {
      console.log(e);
    }
  }

  return (
    <UploaderContainer>
      <ImageList images={images} onClick={onDelete}/>

      {images.length < 1 && (
        <>
          <LabelWrapper>
            {images.length === 0 && <PictureIconSvg/>}
            <FileInputLabel onClick={openFileDialog} disabled={isLoading} loading={isLoading}>
              {images.length === 0 ? t('wall.upload') : t('wall.addPhoto')}
            </FileInputLabel>
          </LabelWrapper>
          <HiddenFileInput
            ref={inputRef}
            id={`image-uploader-${draftParagraph.uuid}`}
            type="file"
            accept="image/*"
            multiple={false}
            onChange={handleFiles}
          />
        </>
      )}
      {images.length >= 1 && (
        <InfoTextInput
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          placeholder={t("wall.imageTextPlaceholder")}
        />
      )}
    </UploaderContainer>
  );
};

export default ImageUploader;
